// src/rms/verticals/operationalDefaults.js
// ============================================================================
// Per-vertical operational defaults
// ============================================================================
// The second half of the vertical preset. presets.js describes WHAT an asset is
// (fields, labels, workflow); this describes HOW the business runs it —
// turnaround, add-ons, cancellation terms, seasonal pricing, checkout questions.
//
// Both are merged by getPreset(), so consumers still see one config object.
// Split into two files purely because one 1,500-line module is unreadable.
//
// Everything here is a STARTING POINT seeded at provisioning. Operators edit it
// afterwards; nothing reads these constants at runtime once an account exists.
//
// The numbers are trade-realistic rather than placeholder: a costume needs three
// days at the cleaner before it can go out again, an RV needs a full day to
// dump, clean and restock, a bike needs fifteen minutes.
// ============================================================================

const day = 1440; // minutes
const hour = 60;

/** Cancellation ladders, most-generous-first. Anything later refunds nothing. */
const POLICY = {
  flexible: {
    name: "Flexible",
    description: "Full refund up to 24 hours before pickup.",
    tiers: [{ daysBefore: 1, refundPct: 100 }],
  },
  moderate: {
    name: "Moderate",
    description: "Full refund up to 7 days before, 50% up to 2 days before.",
    tiers: [
      { daysBefore: 7, refundPct: 100 },
      { daysBefore: 2, refundPct: 50 },
    ],
  },
  strict: {
    name: "Strict",
    description: "50% refund up to 14 days before pickup, nothing after that.",
    tiers: [{ daysBefore: 14, refundPct: 50 }],
  },
  seasonal: {
    name: "Seasonal / event",
    description: "Full refund up to 60 days before, 50% up to 30 days before.",
    tiers: [
      { daysBefore: 60, refundPct: 100 },
      { daysBefore: 30, refundPct: 50 },
    ],
  },
};

// ----------------------------------------------------------------------------
// Paperwork
// ----------------------------------------------------------------------------
// Every trade takes documents at the counter, and every trade takes DIFFERENT
// ones. A passport is fine ID for a costume hire and useless for a car, because
// it does not entitle anyone to drive. A storage operator needs proof of address
// for lien law. A construction yard needs the operator's certification card and
// a certificate of insurance before a machine leaves the gate.
//
// So the list of documents is config, like everything else that varies by trade.
// `key` is what lands in rms_customer_documents.doc_type.

const DOCUMENT = {
  drivers_licence:  { key: "drivers_licence",  label: "Driver's licence",            expires: true,  region: true },
  international_permit: { key: "international_permit", label: "International driving permit", expires: true },
  passport:         { key: "passport",         label: "Passport",                    expires: true },
  national_id:      { key: "national_id",      label: "National ID card",            expires: true },
  proof_of_address: { key: "proof_of_address", label: "Proof of address",            expires: false },
  boating_licence:  { key: "boating_licence",  label: "Boating licence / operator card", expires: true, region: true },
  insurance:        { key: "insurance",        label: "Certificate of insurance",    expires: true,  insurance: true },
  business_licence: { key: "business_licence", label: "Business licence",            expires: true },
  operator_certification: { key: "operator_certification", label: "Operator certification card", expires: true },
  credit_card_auth: { key: "credit_card_auth", label: "Credit card authorisation",   expires: false },
  purchase_order:   { key: "purchase_order",   label: "Purchase order",              expires: false },
  student_id:       { key: "student_id",       label: "Student ID",                  expires: true },
  other:            { key: "other",            label: "Other",                       expires: false },
};

/**
 * @param accepted  offered in the dropdown, in the order staff would reach for them
 * @param required  must be verified and unexpired before checkout
 */
const docs = (accepted, required = []) => ({
  accepted: accepted.map((d) => DOCUMENT[d]).filter(Boolean),
  required,
});

export const DOCUMENT_CATALOGUE = DOCUMENT;

// ----------------------------------------------------------------------------
// Automatic messages
// ----------------------------------------------------------------------------
// Seeded per operator, then theirs to edit. The wording is deliberately plain:
// these are transactional emails a customer needs to act on, not marketing.
// Every one of them answers "what do I do next".
//
// {{placeholders}} are filled by messageService.fillTemplate. An unknown one is
// left visible rather than blanked, so a broken template looks broken instead
// of looking like a sentence with a hole in it.
//
// `arriving` and `returning` differ per trade because the instruction that
// matters differs: a boat renter needs the marina and the safety briefing, a
// storage tenant needs the gate code, a construction site needs a delivery
// window and someone to sign for the machine.

const template = (key, subject, body) => ({ template_key: key, subject, body });

const messages = ({ arriving, returning, deposit }) => [
  template(
    "booking_confirmed",
    "Your booking is confirmed — {{reservation_number}}",
    `Hi {{customer_name}},

You're booked in. Here are the details:

{{asset}}
From {{starts_at}}
Until {{ends_at}}
Total {{total}}

${arriving}

Reply to this email if anything needs changing.

{{business_name}}`,
  ),
  template(
    "pickup_reminder",
    "Coming up: {{asset}} on {{starts_at}}",
    `Hi {{customer_name}},

A quick reminder about your booking {{reservation_number}}.

{{asset}}
From {{starts_at}}
Until {{ends_at}}

${arriving}

See you then.

{{business_name}}`,
  ),
  template(
    "return_reminder",
    "Due back {{ends_at}} — {{asset}}",
    `Hi {{customer_name}},

Just a reminder that {{asset}} is due back on {{ends_at}}.

${returning}

If you need longer, reply and let us know before the return time — it's usually
cheaper than a late fee.

{{business_name}}`,
  ),
  template(
    "invoice_receipt",
    "Your receipt from {{business_name}}",
    `Hi {{customer_name}},

Thanks — that's all settled.

{{asset}}
{{starts_at}} to {{ends_at}}
Total {{total}}

${deposit}

{{business_name}}`,
  ),
  template(
    "balance_due",
    "Outstanding balance — {{reservation_number}}",
    `Hi {{customer_name}},

There's still {{balance}} outstanding on {{reservation_number}}.

If you've already paid, ignore this and accept our apologies — it can take a
day to show. Otherwise, reply here and we'll sort it out.

{{business_name}}`,
  ),
  template(
    "document_expiring",
    "Your paperwork is about to expire",
    `Hi {{customer_name}},

One of the documents we hold for you is close to expiring, so we won't be able
to hire to you until it's renewed.

Send us an up-to-date copy whenever you have it and we'll update your account.

{{business_name}}`,
  ),
];

const MESSAGES = {
  vehicle: messages({
    arriving: "Bring your driving licence — we have to see the original at the counter, and we can't release the vehicle without it.",
    returning: "Please return it with the same amount of fuel, or we'll charge for the difference.",
    deposit: "Your deposit will be released back to your card within 5 working days.",
  }),
  boat: messages({
    arriving: "Arrive 30 minutes early for the safety briefing — it's required before we can let you leave the dock.",
    returning: "Please return to the same slip and top up the fuel before you hand back the keys.",
    deposit: "Your deposit will be released back to your card within 5 working days.",
  }),
  storage: messages({
    arriving: "Your gate code and unit number are on your account. The gate is accessible during posted hours.",
    returning: "Please empty the unit completely and sweep it out — anything left behind is charged for removal.",
    deposit: "Your deposit will be released once we've inspected the unit.",
  }),
  gear: messages({
    arriving: "Please bring photo ID when you collect.",
    returning: "Please check everything is in the case before you bring it back — missing items are charged at replacement cost.",
    deposit: "Your deposit will be released back to your card within 5 working days.",
  }),
  delivered: messages({
    arriving: "We'll deliver to the site address on your booking. Someone needs to be there to sign for it and show the operator certification.",
    returning: "Please have it accessible for collection at the agreed time — waiting time is chargeable.",
    deposit: "Your deposit will be released once we've inspected it on return.",
  }),
  garment: messages({
    arriving: "Please try everything on at collection so we can adjust the fit while you're here.",
    returning: "Don't clean it yourself — return it as-is and we'll take care of that.",
    deposit: "Your deposit will be released once we've checked the garment back in.",
  }),
};

// ----------------------------------------------------------------------------

export const OPERATIONAL_DEFAULTS = {
  // --------------------------------------------------------------------------
  car_rental: {
    messageTemplates: MESSAGES.vehicle,
    documents: docs(["drivers_licence", "international_permit", "passport", "insurance", "credit_card_auth", "other"], ["drivers_licence"]),
    grouping: {
      enabled: true,
      label: "Vehicle class",
      hint: "Customers book a class — Economy, SUV, Van — and you assign the actual car at pickup.",
    },
    turnaroundMinutes: hour,
    bookingMode: "instant",
    measurementSystem: "metric",
    cancellationPolicy: POLICY.moderate,
    usageFeeSchedules: [
      { name: "Standard mileage", metricKey: "odometer", unit: "km", includedPerPeriod: 200, period: "day", flatCentsPerUnit: 25 },
      { name: "Unlimited mileage", metricKey: "odometer", unit: "km", includedPerPeriod: 100000, period: "rental", flatCentsPerUnit: 0 },
    ],
    addons: [
      { name: "Additional driver", priceCents: 1500, chargeType: "per_day" },
      { name: "Child seat", priceCents: 900, chargeType: "per_day", maxQuantity: 3 },
      { name: "GPS unit", priceCents: 800, chargeType: "per_day" },
      { name: "Prepaid fuel", priceCents: 7500, chargeType: "once" },
      { name: "Toll transponder", priceCents: 600, chargeType: "per_day" },
      { name: "Collision damage waiver", priceCents: 2200, chargeType: "per_day" },
    ],
    checkoutQuestions: [
      { prompt: "Will anyone else be driving?", question_type: "boolean",
        triggersAddon: "Additional driver",
        followUp: [
          { key: "driver_name", label: "Additional driver's full name", type: "short_answer", required: true },
          { key: "driver_licence", label: "Their licence number", type: "short_answer", required: true },
        ] },
      { prompt: "Is this trip business or leisure?", question_type: "multiple_choice", options: ["Business", "Leisure"] },
      // The qualifier moved to help_text rather than being deleted: it is the
      // answer to "why are you asking", and a label is not the place for it.
      { prompt: "Flight number", question_type: "short_answer",
        help_text: "If collecting from the airport." },
    ],
    rateRules: [
      { name: "Summer peak", category: "seasonal", travelStartsOn: "06-15", travelEndsOn: "09-05", direction: "increase", kind: "percent", value: 15 },
      { name: "Weekly discount", category: "length_of_rental", tiers: [{ minUnits: 7, direction: "decrease", kind: "percent", value: 15 }, { minUnits: 28, direction: "decrease", kind: "percent", value: 30 }] },
      { name: "Weekend premium", category: "seasonal", daysOfWeek: [5, 6, 7], direction: "increase", kind: "percent", value: 10 },
    ],
  },

  // --------------------------------------------------------------------------
  boat_rental: {
    messageTemplates: MESSAGES.boat,
    documents: docs(["boating_licence", "drivers_licence", "passport", "insurance", "other"], ["drivers_licence"]),
    grouping: {
      enabled: true,
      label: "Vessel class",
      hint: "Pool identical pontoons or bowriders so one booking doesn't block the whole listing.",
    },
    turnaroundMinutes: 90, // refuel, wash down, safety-kit check
    bookingMode: "request",
    measurementSystem: "imperial",
    cancellationPolicy: POLICY.strict,
    usageFeeSchedules: [
      { name: "Engine hours", metricKey: "engine_hours", unit: "hrs", includedPerPeriod: 8, period: "day", flatCentsPerUnit: 4500 },
    ],
    addons: [
      { name: "Fuel package", priceCents: 12000, chargeType: "once" },
      { name: "Licensed captain", priceCents: 35000, chargeType: "per_day" },
      { name: "Water toys package", priceCents: 4500, chargeType: "per_day" },
      { name: "Cooler with ice", priceCents: 2500, chargeType: "once" },
      { name: "Wakeboard / tube", priceCents: 3500, chargeType: "per_day" },
      { name: "Extra life jackets", priceCents: 1000, chargeType: "per_day", maxQuantity: 8 },
    ],
    checkoutQuestions: [
      { prompt: "How would you rate your boating experience?", question_type: "multiple_choice", options: ["First time", "Some experience", "Experienced", "Licensed captain"], is_required: true },
      { prompt: "How many passengers will be aboard?", question_type: "number", is_required: true },
      { prompt: "Do you hold a boating safety certificate?", question_type: "boolean", is_required: true,
        followUp: [
          { key: "certificate_number", label: "Certificate number", type: "short_answer", required: true },
        ] },
    ],
    rateRules: [
      { name: "Peak season", category: "seasonal", travelStartsOn: "06-01", travelEndsOn: "08-31", direction: "increase", kind: "percent", value: 25 },
      { name: "Weekend premium", category: "seasonal", daysOfWeek: [5, 6, 7], direction: "increase", kind: "percent", value: 15 },
      { name: "Multi-day discount", category: "length_of_rental", tiers: [{ minUnits: 3, direction: "decrease", kind: "percent", value: 10 }] },
    ],
  },

  // --------------------------------------------------------------------------
  atv_rental: {
    messageTemplates: MESSAGES.vehicle,
    documents: docs(["drivers_licence", "national_id", "passport", "insurance", "other"], ["drivers_licence"]),
    grouping: { enabled: true, label: "Machine class", hint: "Riders book a machine type; you assign the unit at the trailhead." },
    turnaroundMinutes: hour, // wash, refuel, chain and tyre check
    bookingMode: "request",
    measurementSystem: "imperial",
    cancellationPolicy: POLICY.strict,
    usageFeeSchedules: [
      { name: "Ride hours", metricKey: "engine_hours", unit: "hrs", includedPerPeriod: 6, period: "day", flatCentsPerUnit: 3500 },
    ],
    addons: [
      { name: "Helmet", priceCents: 1500, chargeType: "per_day", maxQuantity: 6 },
      { name: "Goggles & gloves", priceCents: 800, chargeType: "per_day", maxQuantity: 6 },
      { name: "Guided trail tour", priceCents: 9500, chargeType: "per_day" },
      { name: "Trailer rental", priceCents: 6000, chargeType: "per_day" },
      { name: "Fuel package", priceCents: 4000, chargeType: "once" },
      { name: "Damage waiver", priceCents: 3000, chargeType: "per_day" },
    ],
    checkoutQuestions: [
      { prompt: "Riding experience level", question_type: "multiple_choice", options: ["Never ridden", "Beginner", "Intermediate", "Advanced"], is_required: true },
      { prompt: "Ages of all riders", question_type: "short_answer", is_required: true, help_text: "We verify minimum age at pickup." },
      { prompt: "Where do you plan to ride?", question_type: "short_answer" },
    ],
    rateRules: [
      { name: "Weekend premium", category: "seasonal", daysOfWeek: [5, 6, 7], direction: "increase", kind: "percent", value: 20 },
      { name: "Multi-day discount", category: "length_of_rental", tiers: [{ minUnits: 3, direction: "decrease", kind: "percent", value: 15 }] },
    ],
  },

  // --------------------------------------------------------------------------
  bicycle_rental: {
    messageTemplates: MESSAGES.gear,
    documents: docs(["national_id", "drivers_licence", "passport", "student_id", "other"]),
    grouping: {
      enabled: true,
      label: "Bike type & size",
      hint: "A rack of identical medium city bikes is one listing, not fifteen.",
    },
    turnaroundMinutes: 15, // wipe down, tyre and brake check
    bookingMode: "instant",
    measurementSystem: "metric",
    cancellationPolicy: POLICY.flexible,
    usageFeeSchedules: [],
    addons: [
      { name: "Helmet", priceCents: 500, chargeType: "once", maxQuantity: 6 },
      { name: "Lock", priceCents: 400, chargeType: "once" },
      { name: "Lights", priceCents: 400, chargeType: "once" },
      { name: "Child seat", priceCents: 1200, chargeType: "per_day" },
      { name: "Basket / pannier", priceCents: 300, chargeType: "once" },
      { name: "Puncture repair kit", priceCents: 600, chargeType: "once" },
      { name: "Printed route map", priceCents: 500, chargeType: "once" },
    ],
    checkoutQuestions: [
      { prompt: "Rider height (for frame sizing)", question_type: "short_answer", is_required: true },
      { prompt: "Riding with children?", question_type: "boolean",
        triggersAddon: "Child seat",
        followUp: [
          // Three children need three seats. The flag is read by Reservations,
          // so a trade that wants the same behaviour just sets it.
          { key: "children", label: "How many children?", type: "number", required: true, setsAddonQuantity: true },
        ] },
    ],
    rateRules: [
      { name: "Full-day discount", category: "length_of_rental", tiers: [{ minUnits: 6, direction: "decrease", kind: "percent", value: 20 }, { minUnits: 24, direction: "decrease", kind: "percent", value: 35 }] },
      { name: "Summer peak", category: "seasonal", travelStartsOn: "06-01", travelEndsOn: "09-15", direction: "increase", kind: "percent", value: 10 },
    ],
  },

  // --------------------------------------------------------------------------
  storage_unit: {
    messageTemplates: MESSAGES.storage,
    documents: docs(["drivers_licence", "national_id", "proof_of_address", "insurance", "business_licence", "other"], ["proof_of_address"]),
    grouping: {
      enabled: true,
      label: "Unit size",
      hint: "A row of identical 10x10 units is one listing; the tenant is assigned a specific door at move-in.",
    },
    turnaroundMinutes: hour, // sweep and re-lock between tenants
    bookingMode: "instant",
    measurementSystem: "imperial",
    cancellationPolicy: POLICY.flexible, // month-to-month, notice-based
    usageFeeSchedules: [],
    addons: [
      { name: "Disc lock", priceCents: 1800, chargeType: "once" },
      { name: "Tenant contents insurance", priceCents: 1200, chargeType: "once", isRequired: true },
      { name: "24-hour access upgrade", priceCents: 1500, chargeType: "once" },
      { name: "Moving box bundle", priceCents: 3500, chargeType: "once" },
      { name: "Dolly hire", priceCents: 0, chargeType: "once" },
      { name: "Administration fee", priceCents: 2500, chargeType: "once", isRequired: true },
    ],
    checkoutQuestions: [
      { prompt: "What will you be storing?", question_type: "long_answer", is_required: true, help_text: "We cannot accept hazardous or perishable goods." },
      { prompt: "Do you need 24-hour gate access?", question_type: "boolean",
        triggersAddon: "24-hour access upgrade" },
      { prompt: "Roughly how long do you expect to rent?", question_type: "multiple_choice", options: ["1-3 months", "3-6 months", "6-12 months", "Over a year"] },
      { prompt: "Emergency contact name and phone", question_type: "short_answer", is_required: true },
    ],
    rateRules: [
      { name: "First month half price", category: "search_window", direction: "decrease", kind: "percent", value: 50, tiers: [{ minUnits: 1, maxUnits: 1, direction: "decrease", kind: "percent", value: 50 }] },
      { name: "Long-stay discount", category: "length_of_rental", tiers: [{ minUnits: 6, direction: "decrease", kind: "percent", value: 5 }, { minUnits: 12, direction: "decrease", kind: "percent", value: 10 }] },
    ],
  },

  // --------------------------------------------------------------------------
  tool_rental: {
    messageTemplates: MESSAGES.gear,
    documents: docs(["drivers_licence", "national_id", "proof_of_address", "credit_card_auth", "other"]),
    grouping: { enabled: true, label: "Tool model", hint: "Six identical breakers are one listing with six units." },
    turnaroundMinutes: 30, // clean, test, recharge
    bookingMode: "instant",
    measurementSystem: "imperial",
    cancellationPolicy: POLICY.flexible,
    usageFeeSchedules: [
      { name: "Runtime hours", metricKey: "runtime_hours", unit: "hrs", includedPerPeriod: 8, period: "day", flatCentsPerUnit: 0 },
    ],
    addons: [
      { name: "Blades & consumables", priceCents: 2500, chargeType: "once" },
      { name: "Safety kit (glasses, gloves, ear defenders)", priceCents: 1000, chargeType: "once" },
      { name: "Delivery & collection", priceCents: 4500, chargeType: "once" },
      { name: "Damage waiver", priceCents: 1200, chargeType: "per_day" },
      { name: "Spare battery", priceCents: 1500, chargeType: "per_day" },
      { name: "Cleaning fee (returned dirty)", priceCents: 3000, chargeType: "once", isHidden: true },
    ],
    checkoutQuestions: [
      { prompt: "What job are you using this for?", question_type: "short_answer" },
      { prompt: "Do you need delivery?", question_type: "boolean",
        triggersAddon: "Delivery & collection",
        followUp: [
          { key: "delivery_address", label: "Delivery address", type: "long_answer", required: true },
        ] },
      { prompt: "Have you used this type of tool before?", question_type: "boolean", help_text: "We'll run through operation at the counter if not." },
    ],
    rateRules: [
      { name: "Weekend rate", category: "seasonal", daysOfWeek: [5, 6, 7], direction: "decrease", kind: "percent", value: 10 },
      { name: "Weekly discount", category: "length_of_rental", tiers: [{ minUnits: 7, direction: "decrease", kind: "percent", value: 25 }, { minUnits: 28, direction: "decrease", kind: "percent", value: 40 }] },
    ],
  },

  // --------------------------------------------------------------------------
  party_equipment: {
    messageTemplates: MESSAGES.delivered,
    documents: docs(["drivers_licence", "national_id", "business_licence", "insurance", "purchase_order", "other"]),
    grouping: {
      enabled: true,
      label: "Item type",
      hint: "This is the vertical grouping was built for — 200 identical chairs are one listing with a quantity, not 200 records.",
    },
    turnaroundMinutes: 4 * hour, // clean, count, repack
    bookingMode: "request",
    measurementSystem: "imperial",
    cancellationPolicy: POLICY.seasonal,
    usageFeeSchedules: [],
    addons: [
      { name: "Delivery & collection", priceCents: 15000, chargeType: "once", isRequired: true },
      { name: "Setup & teardown crew", priceCents: 35000, chargeType: "once" },
      { name: "Linens", priceCents: 800, chargeType: "per_unit" },
      { name: "Lighting package", priceCents: 20000, chargeType: "once" },
      { name: "Generator", priceCents: 12000, chargeType: "per_day" },
      { name: "Damage waiver", priceCents: 7500, chargeType: "once" },
      { name: "Rush order surcharge", priceCents: 20000, chargeType: "once", isHidden: true },
    ],
    checkoutQuestions: [
      { prompt: "What type of event is this?", question_type: "multiple_choice", options: ["Wedding", "Corporate", "Birthday", "Festival", "Funeral", "Other"], is_required: true },
      { prompt: "Expected guest count", question_type: "number", is_required: true },
      { prompt: "Venue address", question_type: "long_answer", is_required: true },
      { prompt: "Is the site accessible by a box truck?", question_type: "boolean", is_required: true, help_text: "Affects delivery crew and pricing.",
        triggerValue: "No",
        followUp: [
          { key: "site_access", label: "How should the crew reach the site?", type: "long_answer", required: true },
        ] },
      { prompt: "What time can we access the venue for setup?", question_type: "short_answer" },
    ],
    rateRules: [
      { name: "Wedding season", category: "seasonal", travelStartsOn: "05-01", travelEndsOn: "10-15", direction: "increase", kind: "percent", value: 20 },
      { name: "Weekend premium", category: "seasonal", daysOfWeek: [5, 6, 7], direction: "increase", kind: "percent", value: 25 },
      { name: "Midweek discount", category: "seasonal", daysOfWeek: [1, 2, 3, 4], direction: "decrease", kind: "percent", value: 15 },
    ],
  },

  // --------------------------------------------------------------------------
  photo_av_gear: {
    messageTemplates: MESSAGES.gear,
    documents: docs(["drivers_licence", "passport", "insurance", "business_licence", "credit_card_auth", "student_id", "other"], ["insurance"]),
    grouping: { enabled: true, label: "Kit model", hint: "Three identical camera bodies are one listing with three units." },
    turnaroundMinutes: 2 * hour, // function test, sensor clean, charge
    bookingMode: "request",
    measurementSystem: "metric",
    cancellationPolicy: POLICY.moderate,
    usageFeeSchedules: [
      { name: "Shutter actuations", metricKey: "shutter_count", unit: "actuations", includedPerPeriod: 100000, period: "rental", flatCentsPerUnit: 0 },
    ],
    addons: [
      { name: "Equipment insurance waiver", priceCents: 4500, chargeType: "per_day" },
      { name: "Spare batteries", priceCents: 1200, chargeType: "per_day", maxQuantity: 6 },
      { name: "Memory cards", priceCents: 1500, chargeType: "per_day", maxQuantity: 6 },
      { name: "Hard flight case", priceCents: 2000, chargeType: "per_day" },
      { name: "Delivery to set", priceCents: 8500, chargeType: "once" },
      { name: "Rush prep (same day)", priceCents: 10000, chargeType: "once" },
    ],
    checkoutQuestions: [
      { prompt: "Production name", question_type: "short_answer", is_required: true },
      { prompt: "Shoot location", question_type: "short_answer", is_required: true },
      { prompt: "Certificate of insurance provider and policy number", question_type: "short_answer", is_required: true },
      { prompt: "Do you need delivery to set?", question_type: "boolean",
        triggersAddon: "Delivery to set",
        followUp: [
          { key: "set_address", label: "Set address", type: "long_answer", required: true },
        ] },
    ],
    rateRules: [
      { name: "Weekend production premium", category: "seasonal", daysOfWeek: [5, 6, 7], direction: "increase", kind: "percent", value: 20 },
      { name: "Weekly rate (3-day week)", category: "length_of_rental", tiers: [{ minUnits: 5, direction: "decrease", kind: "percent", value: 35 }], help: "Industry convention: a week bills as three days." },
    ],
  },

  // --------------------------------------------------------------------------
  costume_formalwear: {
    messageTemplates: MESSAGES.garment,
    documents: docs(["national_id", "drivers_licence", "credit_card_auth", "other"]),
    grouping: { enabled: true, label: "Garment & size", hint: "Four identical 40R tuxedos are one listing with four units." },
    // Three days at the cleaner before it can go out again. The longest
    // turnaround of any vertical, and the reason turnaround is a real feature.
    turnaroundMinutes: 3 * day,
    bookingMode: "request",
    measurementSystem: "imperial",
    cancellationPolicy: POLICY.strict,
    usageFeeSchedules: [],
    addons: [
      { name: "Cleaning fee", priceCents: 3500, chargeType: "once", isRequired: true },
      { name: "Alterations", priceCents: 6000, chargeType: "once" },
      { name: "Shoes", priceCents: 2500, chargeType: "once" },
      { name: "Accessories set (tie, cufflinks, pocket square)", priceCents: 2000, chargeType: "once" },
      { name: "Garment bag", priceCents: 1000, chargeType: "once" },
      { name: "Rush fitting", priceCents: 5000, chargeType: "once" },
      { name: "Damage waiver", priceCents: 2500, chargeType: "once" },
    ],
    checkoutQuestions: [
      { prompt: "What is the event and date?", question_type: "short_answer", is_required: true },
      { prompt: "Chest, waist and inseam measurements", question_type: "short_answer", is_required: true },
      { prompt: "Do you need alterations?", question_type: "boolean",
        triggersAddon: "Alterations",
        followUp: [
          { key: "alteration_notes", label: "What needs altering?", type: "long_answer", required: true },
        ] },
      { prompt: "When would you like to collect?", question_type: "short_answer" },
    ],
    rateRules: [
      { name: "Prom season", category: "seasonal", travelStartsOn: "04-01", travelEndsOn: "06-30", direction: "increase", kind: "percent", value: 25 },
      { name: "Halloween", category: "seasonal", travelStartsOn: "10-01", travelEndsOn: "11-02", direction: "increase", kind: "percent", value: 30 },
      { name: "Off-season", category: "seasonal", travelStartsOn: "01-05", travelEndsOn: "03-15", direction: "decrease", kind: "percent", value: 20 },
    ],
  },

  // --------------------------------------------------------------------------
  camping_gear: {
    messageTemplates: MESSAGES.gear,
    documents: docs(["drivers_licence", "national_id", "passport", "credit_card_auth", "other"]),
    grouping: { enabled: true, label: "Gear model", hint: "Ten identical 2-person tents are one listing with ten units." },
    turnaroundMinutes: 2 * day, // must be fully dried before it can go back out
    bookingMode: "instant",
    measurementSystem: "metric",
    cancellationPolicy: POLICY.flexible, // weather-dependent trade
    usageFeeSchedules: [],
    addons: [
      { name: "Cleaning & drying fee", priceCents: 2000, chargeType: "once" },
      { name: "Fuel canister", priceCents: 800, chargeType: "once", maxQuantity: 4 },
      { name: "Sleeping bag liner", priceCents: 600, chargeType: "per_day" },
      { name: "Camp chairs", priceCents: 500, chargeType: "per_day", maxQuantity: 8 },
      { name: "Cooler", priceCents: 1000, chargeType: "per_day" },
      { name: "Bear canister", priceCents: 800, chargeType: "per_day" },
      { name: "Damage waiver", priceCents: 1500, chargeType: "once" },
    ],
    checkoutQuestions: [
      { prompt: "Where are you camping?", question_type: "short_answer" },
      { prompt: "How many in your group?", question_type: "number", is_required: true },
      { prompt: "Have you used this type of gear before?", question_type: "boolean", help_text: "We'll demo pitching and stove use if not." },
    ],
    rateRules: [
      { name: "Peak summer", category: "seasonal", travelStartsOn: "06-15", travelEndsOn: "09-05", direction: "increase", kind: "percent", value: 15 },
      { name: "Weekly discount", category: "length_of_rental", tiers: [{ minUnits: 7, direction: "decrease", kind: "percent", value: 25 }] },
      { name: "Shoulder season", category: "seasonal", travelStartsOn: "10-01", travelEndsOn: "04-30", direction: "decrease", kind: "percent", value: 20 },
    ],
  },

  // --------------------------------------------------------------------------
  rv_rental: {
    messageTemplates: MESSAGES.vehicle,
    documents: docs(["drivers_licence", "international_permit", "passport", "insurance", "credit_card_auth", "other"], ["drivers_licence", "insurance"]),
    grouping: { enabled: true, label: "RV class", hint: "Identical Class C units pool under one listing; you assign the coach at pickup." },
    turnaroundMinutes: day, // dump tanks, deep clean, restock, service check
    bookingMode: "request",
    measurementSystem: "imperial",
    cancellationPolicy: POLICY.strict,
    usageFeeSchedules: [
      { name: "Standard mileage", metricKey: "odometer", unit: "km", includedPerPeriod: 150, period: "day", flatCentsPerUnit: 45 },
      { name: "Unlimited mileage", metricKey: "odometer", unit: "km", includedPerPeriod: 100000, period: "rental", flatCentsPerUnit: 0 },
      { name: "Generator hours", metricKey: "generator_hours", unit: "hrs", includedPerPeriod: 0, period: "day", flatCentsPerUnit: 500 },
    ],
    addons: [
      { name: "Prep & cleaning fee", priceCents: 15000, chargeType: "once", isRequired: true },
      { name: "Kitchen kit", priceCents: 7500, chargeType: "once" },
      { name: "Bedding package", priceCents: 6000, chargeType: "once", maxQuantity: 6 },
      { name: "Unlimited mileage upgrade", priceCents: 4500, chargeType: "per_day" },
      { name: "Propane refill", priceCents: 4500, chargeType: "once" },
      { name: "Camp chairs & table", priceCents: 2500, chargeType: "once" },
      { name: "Roadside assistance", priceCents: 1200, chargeType: "per_day" },
    ],
    checkoutQuestions: [
      { prompt: "Where are you planning to travel?", question_type: "long_answer", is_required: true },
      { prompt: "How many travellers?", question_type: "number", is_required: true },
      { prompt: "Driver's licence class", question_type: "short_answer", is_required: true },
      { prompt: "Will you be towing anything?", question_type: "boolean",
        followUp: [
          { key: "towed_item", label: "What are you towing?", type: "short_answer", required: true },
          { key: "towed_weight", label: "Loaded weight (lb)", type: "number", required: false },
        ] },
      { prompt: "Have you driven an RV before?", question_type: "boolean", help_text: "We include a walkthrough either way; this sets how long to allow." },
    ],
    rateRules: [
      { name: "Peak summer", category: "seasonal", travelStartsOn: "06-15", travelEndsOn: "09-05", direction: "increase", kind: "percent", value: 30 },
      { name: "Weekly discount", category: "length_of_rental", tiers: [{ minUnits: 7, direction: "decrease", kind: "percent", value: 15 }, { minUnits: 28, direction: "decrease", kind: "percent", value: 30 }] },
      { name: "Shoulder season", category: "seasonal", travelStartsOn: "10-01", travelEndsOn: "04-30", direction: "decrease", kind: "percent", value: 20 },
      { name: "Minimum stay in peak", category: "minimum_duration", travelStartsOn: "06-15", travelEndsOn: "09-05", minDurationUnits: 3 },
    ],
  },

  // --------------------------------------------------------------------------
  construction_equipment: {
    messageTemplates: MESSAGES.delivered,
    documents: docs(["operator_certification", "drivers_licence", "insurance", "business_licence", "purchase_order", "other"], ["operator_certification", "insurance"]),
    grouping: { enabled: true, label: "Machine model", hint: "Four identical skid steers pool under one listing." },
    turnaroundMinutes: 8 * hour, // service, wash, inspect, refuel
    bookingMode: "request",
    measurementSystem: "metric",
    cancellationPolicy: POLICY.moderate,
    usageFeeSchedules: [
      { name: "Engine hours", metricKey: "engine_hours", unit: "hrs", includedPerPeriod: 8, period: "day", flatCentsPerUnit: 6500 },
    ],
    addons: [
      { name: "Delivery & pickup", priceCents: 25000, chargeType: "once", isRequired: true },
      { name: "Damage waiver", priceCents: 6500, chargeType: "per_day" },
      { name: "Certified operator", priceCents: 55000, chargeType: "per_day" },
      { name: "Additional attachment", priceCents: 8500, chargeType: "per_day", maxQuantity: 3 },
      { name: "Fuel service", priceCents: 12000, chargeType: "once" },
      { name: "Oversize permit handling", priceCents: 17500, chargeType: "once" },
      { name: "After-hours collection", priceCents: 15000, chargeType: "once" },
    ],
    checkoutQuestions: [
      { prompt: "Job site address", question_type: "long_answer", is_required: true },
      { prompt: "Site contact name and mobile", question_type: "short_answer", is_required: true },
      { prompt: "Operator certification number", question_type: "short_answer", is_required: true, help_text: "Must match a verified certificate on your account." },
      { prompt: "Ground conditions at the site", question_type: "multiple_choice", options: ["Paved", "Compacted gravel", "Soft ground / mud", "Slope"], is_required: true },
      { prompt: "Any overhead power lines or access restrictions?", question_type: "long_answer", is_required: true },
    ],
    rateRules: [
      { name: "Weekly discount", category: "length_of_rental", tiers: [{ minUnits: 7, direction: "decrease", kind: "percent", value: 20 }, { minUnits: 28, direction: "decrease", kind: "percent", value: 35 }] },
      { name: "Peak build season", category: "seasonal", travelStartsOn: "04-15", travelEndsOn: "10-31", direction: "increase", kind: "percent", value: 10 },
      { name: "Winter discount", category: "seasonal", travelStartsOn: "12-01", travelEndsOn: "03-01", direction: "decrease", kind: "percent", value: 15 },
      { name: "High utilisation surge", category: "utilization", utilizationMinPct: 80, direction: "increase", kind: "percent", value: 15 },
    ],
  },
};

export const CANCELLATION_POLICIES = POLICY;

/** Operational defaults for a vertical, or a safe empty shape. */
export function operationalDefaultsFor(verticalKey) {
  return (
    OPERATIONAL_DEFAULTS[verticalKey] || {
      // A vertical with no preset still needs somewhere to record ID, so the
      // fallback offers the universal documents rather than an empty dropdown.
      documents: docs(["drivers_licence", "national_id", "passport", "insurance", "other"]),
      messageTemplates: MESSAGES.gear,
      grouping: { enabled: false },
      turnaroundMinutes: 0,
      bookingMode: "request",
      measurementSystem: "metric",
      cancellationPolicy: POLICY.moderate,
      usageFeeSchedules: [],
      addons: [],
      checkoutQuestions: [],
      rateRules: [],
    }
  );
}

export default OPERATIONAL_DEFAULTS;
